import { Css, TagType } from "@homebound/beam";
import {
  BidPackageStatus,
  CostCodeBidSummaryFlag,
  CostCodeBidSummaryStatus,
  CostType,
  InternalEstimateType,
} from "src/generated/graphqlTypes";
import {
  createBidPackagesDetailUrl,
  createBidPackagesEditUrl,
  createBidPackageUrl,
  createPlanBasedBidPackageEditUrl,
  createPlanBasedBidPackageUrl,
} from "src/RouteUrls";
import {
  BidPackagesPageV2_BidPackageFragment,
  BidPackagesPageV2_CostCodeBidSummaryFragment,
} from "./BidPackagesPageV2.generated";

/** Caps both the summaries and the bid packages loaded for a community. */
export const BID_PACKAGES_PAGE_LIMIT = 5_000;

export function bidPackageCreateOptions(projectId: string) {
  return [
    { label: "Create Plan Based Package", url: createPlanBasedBidPackageUrl({ projectId }) },
    { label: "Create Unit Based Package", url: createBidPackageUrl("add", { projectId }) },
  ];
}

/**
 * Only some of a group's product offerings have a bid. The BE flags each product offering on its own
 * (`MISSING_BID`), so this partial coverage only shows once the FE groups them.
 */
export const productOfferingsNotCovered = "PRODUCT_OFFERINGS_NOT_COVERED" as const;
export type GroupFlag = CostCodeBidSummaryFlag | typeof productOfferingsNotCovered;

const statusTagType: Record<CostCodeBidSummaryStatus, TagType> = {
  [CostCodeBidSummaryStatus.Missing]: "error",
  [CostCodeBidSummaryStatus.InProgress]: "neutral",
  [CostCodeBidSummaryStatus.Incomplete]: "warning",
  [CostCodeBidSummaryStatus.Complete]: "success",
};

/** Missing and incomplete use solid palette colors; in progress and complete keep Beam's tag types. */
export function statusTag(code: CostCodeBidSummaryStatus) {
  const xss =
    code === CostCodeBidSummaryStatus.Missing
      ? Css.bgRed600.white.$
      : code === CostCodeBidSummaryStatus.Incomplete
        ? Css.bgOrange500.white.$
        : undefined;
  return { type: statusTagType[code], ...(xss && { xss }) };
}

/** Project estimates are gray 200; provisional estimates are yellow 100. Untyped estimates stay yellow 200. */
export function estimateSourceTag(code: InternalEstimateType | null | undefined) {
  if (code === InternalEstimateType.Project) return { type: "neutral" as const };
  if (code === InternalEstimateType.Provisional) return { type: "update" as const, xss: Css.bgYellow100.$ };
  return { type: "update" as const };
}

type Summary = BidPackagesPageV2_CostCodeBidSummaryFragment;

/** How many product offerings have no bid at all, out of those with scope for the cost code + cost type. */
type Uncovered = { missing: number; total: number };

/** A cost code + cost type across the community, built from the BE's per POF summaries. */
export type CostCodeBidSummaryGroup = {
  id: string;
  costCode: Summary["costCode"];
  costType: Summary["costType"];
  /** De-duped across the product offerings, awarded first. */
  bidPackages: BidPackagesPageV2_BidPackageFragment[];
  /** The most severe status across the product offerings. */
  status: Summary["status"];
  /** The union of the product offerings' flags; `flagBanners` decides the order they stack in. */
  flags: GroupFlag[];
  uncovered: Uncovered;
};

const costTypeOrder = [CostType.Labor, CostType.Materials, CostType.Subcontractor];
/** Most severe first: a product offering that isn't awarded yet hides how complete the others are. */
const statusSeverity = [
  CostCodeBidSummaryStatus.Missing,
  CostCodeBidSummaryStatus.InProgress,
  CostCodeBidSummaryStatus.Incomplete,
  CostCodeBidSummaryStatus.Complete,
];
export const bidPackageStatusRank = [
  BidPackageStatus.Awarded,
  BidPackageStatus.Received,
  BidPackageStatus.Open,
  BidPackageStatus.Draft,
];
/** The flags that mean a product offering has no bid at all. */
export const uncoveredFlags = [CostCodeBidSummaryFlag.MissingBid, CostCodeBidSummaryFlag.MissingRequiredBid];

export function groupCostCodeBidSummaries(
  summaries: Summary[],
  bidPackages: BidPackagesPageV2_BidPackageFragment[],
): CostCodeBidSummaryGroup[] {
  const bidPackagesById = bidPackages.keyBy((bp) => bp.id);
  return Object.values(summaries.groupBy((s) => `${s.costCode.id}_${s.costType.code}`))
    .map((group) => {
      const [{ costCode, costType }] = group;
      const uncovered = uncoveredProductOfferings(group);
      return {
        id: `${costCode.id}_${costType.code}`,
        costCode,
        costType,
        // A package we have no details for is dropped instead of shown as a bare row.
        bidPackages: group
          .flatMap((s) => s.bidPackages)
          .uniqueByKey("id")
          .map((bp) => bidPackagesById[bp.id])
          .compact()
          .sortBy((bp) => [bidPackageStatusRank.indexOf(bp.status.code), bp.name]),
        status: mostSevereStatus(group),
        flags: groupFlags(group, uncovered),
        uncovered,
      };
    })
    .sortBy((g) => [g.costCode.number, costTypeOrder.indexOf(g.costType.code)]);
}

/** The most severe of the product offerings' statuses. */
function mostSevereStatus(summaries: Pick<Summary, "status">[]): Summary["status"] {
  return summaries.minBy((s) => statusSeverity.indexOf(s.status.code)).status;
}

/**
 * The union of the product offerings' flags. A missing bid on only some of them means the group is partially
 * covered rather than missing, which becomes the FE-only `productOfferingsNotCovered` flag.
 */
function groupFlags(summaries: Pick<Summary, "flags">[], { missing, total }: Uncovered): GroupFlag[] {
  const flags = summaries.flatMap((s) => s.flags).unique();
  const partiallyCovered = missing > 0 && missing < total;
  return partiallyCovered
    ? [productOfferingsNotCovered, ...flags.filter((f) => !uncoveredFlags.includes(f))]
    : // If we are missing bids then we should only show the missing bid flag
      flags.includes(CostCodeBidSummaryFlag.MissingBid)
      ? [CostCodeBidSummaryFlag.MissingBid]
      : flags;
}

function uncoveredProductOfferings(summaries: Pick<Summary, "flags">[]): Uncovered {
  return {
    missing: summaries.filter((s) => s.flags.some((f) => uncoveredFlags.includes(f))).length,
    total: summaries.length,
  };
}

type BidPackage = BidPackagesPageV2_BidPackageFragment;

/** The most recently awarded bid package, which the post-award flags are about; else the first one listed. */
export function lastAwardedBidPackage<T extends Pick<BidPackage, "status" | "lastAwardedAt">>(bidPackages: T[]) {
  return (
    bidPackages
      .filter((bp) => bp.status.code === BidPackageStatus.Awarded && bp.lastAwardedAt)
      .maxBy((bp) => bp.lastAwardedAt!) ?? bidPackages.first
  );
}

/** The (plan-based or legacy) edit flow of a bid package. */
export function bidPackageEditHref(
  { isPlanBasedV2, bidPackageGroup }: Pick<BidPackage, "isPlanBasedV2" | "bidPackageGroup">,
  projectId: string,
) {
  const createEditUrl = isPlanBasedV2 ? createPlanBasedBidPackageEditUrl : createBidPackagesEditUrl;
  return createEditUrl(bidPackageGroup!.id, projectId);
}

/** First drafts link to the edit flow; everything else links to the detail page. */
export function bidPackageHref(
  bp: Pick<BidPackage, "id" | "isPlanBasedV2" | "latestVersion" | "status" | "bidPackageGroup">,
  projectId: string,
) {
  const { id, latestVersion, status, bidPackageGroup } = bp;
  const firstDraft = latestVersion.version === 1 && status.code === BidPackageStatus.Draft;
  return firstDraft ? bidPackageEditHref(bp, projectId) : createBidPackagesDetailUrl(bidPackageGroup!.id, id);
}
